import * as React from 'react';
import { connect } from 'react-redux';
import { createStyles, withStyles } from '@material-ui/core/styles';
import { Button, CircularProgress } from '@material-ui/core';
import EventEmitter from '../../../lib/eventEmitter';
import Carousel from 'nuka-carousel';

import {
  SwiperDelete,
  SwiperBack,
  SwiperFavorites,
  FiltersIcon,
  MapIcon,
} from '../Icons';
import { saveFavorite } from '../../../actions/favorite';
import { blockListing } from '../../../actions/blocked';
import { media } from '../../../styles/base';
import NoResults from '../NoResults';
import ToriiLoader from '../ToriiLoader';
import CarouselComponent from './CarouselComponent';
import SaveSearchButton from '../../pages/search/SaveSearchButton';

import style from '../../../styles/sass/components/Swiper.module.scss';

interface Props {
  blockListing: Function;
  classes: any;
  fetchMore?: Function;
  hideFilter?: boolean;
  listings: any[];
  loading?: boolean;
  saveFavorite: Function;
  showMapIcon?: boolean;
  showSaveSearchIcon?: boolean;
  splitUp?: boolean;
  token: string;
  triggerSettings?: Function;
  triggerView?: Function;
  renderFilterCountBadge?: Function;
}

interface State {
  currentIndex: number;
  feed: any[];
  loading: boolean;
  previousFeed: any[];
  showAnimation: boolean;
}

const styles = createStyles({
  button: {
    display: 'flex',
    flexDirection: 'column',
  },
  buttonText: {
    color: '#323232',
    fontSize: 10,
    margin: 0,
    '&:hover': {
      color: '#2C2C2C',
    },
  },
  buttonContainer: {
    alignItems: 'center',
    alignSelf: 'center',
    bottom: 80,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    position: 'absolute',
    width: '100%',
    [media.mobile]: {
      bottom: 15,
    },
  },
  buttonLarge: {
    background:
      'radial-gradient(circle, rgba(44,199,164,0) 0%, rgba(44,199,164,0) 71.07%, #50FFD7 100%)',
    borderRadius: '50%',
    boxShadow: '0 2px 7px 0 rgba(0,0,0,0.25)',
    height: 65,
    marginLeft: 10,
    marginRight: 10,
    padding: 0,
    position: 'relative',
    width: 65,
  },
  buttonLargeDisabled: {
    borderRadius: '50%',
    height: 65,
    marginLeft: 10,
    marginRight: 10,
    padding: 0,
    width: 65,
  },
  fabProgress: {
    borderRadius: '50%',
    color: 'green',
    height: 83,
    position: 'absolute',
    width: 83,
  },
  mapButton: {
    alignItems: 'center',
    display: 'flex',
    height: 20,
    justifyContent: 'center',
    marginLeft: 10,
    minWidth: 20,
    padding: 0,
  },
  settingsButton: {
    alignItems: 'center',
    display: 'flex',
    height: 20,
    justifyContent: 'center',
    marginRight: 10,
    minWidth: 20,
    padding: 0,
  },
});

const LISTINGS_COUNT = 10;
const BUFFER_COUNT = 3;

export class Swiper extends React.Component<Props, State> {
  feedToUse = new Array();
  constructor(props: Props) {
    super(props);
    this.state = {
      currentIndex: 0,
      feed: [],
      loading: this.props.loading || false,
      previousFeed: [],
      showAnimation: false,
    };
  }

  animate = (callback?: Function) => {
    if (!this.state.showAnimation) {
      this.setState({ showAnimation: true });
      setTimeout(() => {
        this.setState({ showAnimation: false });
        if (callback) {
          callback();
        }
      }, 700);
    }
  };

  setFeedToState = (listings: any) => {
    this.feedToUse = Object.assign([], listings);
    this.setState({
      feed: this.props.splitUp
        ? this.feedToUse.splice(0, LISTINGS_COUNT)
        : this.feedToUse,
    });
  };

  componentDidMount() {
    this.setFeedToState(this.props.listings);
    if (typeof window !== 'undefined') {
      document.addEventListener('keydown', this.handleKeyPress, false);
    }
    EventEmitter.on('refreshSwiper', this.refreshSwiperHandler);
  }

  componentDidUpdate(prevProps: Props) {
    if (!this.props.splitUp) {
      if (prevProps.listings.length !== this.props.listings.length) {
        this.setFeedToState(this.props.listings);
      }
    }
  }

  refreshSwiperHandler = (listings: any) => {
    this.setFeedToState(listings);
  };

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      document.removeEventListener('keydown', this.handleKeyPress, false);
    }
    EventEmitter.removeListener('refreshSwiper', this.refreshSwiperHandler);
  }

  handleKeyPress = (e: any) => {
    if (e.keyCode === 39) {
      // Right arrow
      this.handleFavoriteListing();
    } else if (e.keyCode === 37) {
      // Left arrow
      this.handleBlockListing();
    } else if (e.keyCode === 8) {
      // Backspace key
      this.previousListing();
    }
  };

  _fetchMore = () => {
    this.props.fetchMore && this.props.fetchMore();
  };

  appendListing = () => {
    if (this.feedToUse.length > 0) {
      const feedToAppend = this.feedToUse.splice(0, LISTINGS_COUNT);
      if (feedToAppend.length > 0) {
        this.setState({
          feed: this.state.feed.concat(feedToAppend),
        });
      }
    } else {
      this.setState({
        currentIndex: this.state.currentIndex + 1,
      });
    }
  };

  nextListing = () => {
    if (this.props.splitUp) {
      if (
        this.state.currentIndex === this.state.feed.length - BUFFER_COUNT &&
        this.state.feed.length !== BUFFER_COUNT
      ) {
        this.setState(
          {
            currentIndex: this.state.currentIndex + 1,
          },
          () => {
            this.appendListing();
          },
        );
      }
      if (this.state.currentIndex === this.state.feed.length - 1) {
        this.setState({
          feed: [],
          previousFeed: [],
        });
      } else {
        this.setState({
          currentIndex: this.state.currentIndex + 1,
        });
      }
    } else {
      if (this.state.currentIndex < this.state.feed.length - 2) {
        this.setState({
          currentIndex: this.state.currentIndex + 1,
        });
      } else if (this.state.currentIndex === this.state.feed.length - 1) {
        this.setState({
          feed: [],
          previousFeed: [],
        });
      } else {
        this.setState({
          currentIndex: this.state.currentIndex + 1,
        });
        this._fetchMore();
      }
    }
  };

  handleFavoriteListing = () => {
    this.animate(() => {
      const { saveFavorite, token } = this.props;
      const { currentIndex, feed } = this.state;
      const currentListing = feed[currentIndex];
      const previousFeed = currentListing;
      if (token) {
        saveFavorite(currentListing.listingMlsId, token);
      }
      this.setState(
        {
          previousFeed: [previousFeed, ...this.state.previousFeed],
        },
        () => {
          this.nextListing();
        },
      );
    });
  };

  showSettings = () => {
    this.props.triggerSettings && this.props.triggerSettings();
  };

  onMapPress = () => {
    this.props.triggerView && this.props.triggerView();
  };

  handleBlockListing = () => {
    const { blockListing, token } = this.props;
    const { currentIndex, feed } = this.state;
    const currentListing = feed[currentIndex];
    const previousFeed = currentListing;
    if (token) {
      blockListing(currentListing.listingMlsId, token);
    }
    this.setState(
      {
        previousFeed: [previousFeed, ...this.state.previousFeed],
      },
      () => {
        this.nextListing();
      },
    );
  };

  previousListing = () => {
    if (this.state.currentIndex !== 0) {
      this.setState({
        currentIndex: this.state.currentIndex - 1,
        previousFeed: this.state.previousFeed.slice(
          1,
          this.state.previousFeed.length,
        ),
      });
    }
  };

  createCarousel = () => {
    return this.state.feed.map((listing: any, index: number) => (
      <CarouselComponent
        key={index}
        listing={listing}
        index={index}
        currentListingIndex={this.state.currentIndex}
      />
    ));
  };

  render() {
    const { classes, hideFilter, showMapIcon, showSaveSearchIcon } = this.props;
    const { feed, loading, previousFeed } = this.state;
    const empty = feed.length === 0 || loading;
    const backButtonDisabled = empty || previousFeed.length === 0;
    return (
      <React.Fragment>
        {loading ? (
          <ToriiLoader padTop={150} />
        ) : !empty ? (
          <Carousel
            autoplay={false}
            cellAlign="center"
            cellSpacing={50}
            className={'swiper-carousel'}
            dragging={false}
            heightMode="max"
            pauseOnHover={false}
            slideIndex={this.state.currentIndex}
            slidesToShow={1}
            swiping={false}
            withoutControls
          >
            {this.createCarousel()}
          </Carousel>
        ) : (
          <NoResults
            emptyText="Oops! Looks like there aren't any homes matching your search. Try adjusting your filters to find more homes."
            padTop
          />
        )}
        {!hideFilter && (
          <Button
            className={`${style.mobileFiltersFab} mobile-only`}
            classes={{ label: classes.button }}
            onClick={this.showSettings}
          >
            {this.props.renderFilterCountBadge &&
              this.props.renderFilterCountBadge()}
            <FiltersIcon dark />
            <p className={classes.buttonText}>Filters</p>
          </Button>
        )}
        {showMapIcon && (
          <Button
            className={`${style.mobileSwiperFab} mobile-only`}
            classes={{ label: classes.button }}
            onClick={this.onMapPress}
          >
            <MapIcon fillColor={'black'} />
            <p className={classes.buttonText}>Map</p>
          </Button>
        )}
        {showSaveSearchIcon && (
          <SaveSearchButton mobileButton mobileClasses={classes} />
        )}
        <div className={classes.buttonContainer}>
          <Button
            className={
              empty ? classes.buttonLargeDisabled : classes.buttonLarge
            }
            onClick={this.handleBlockListing}
            disabled={empty}
          >
            <SwiperDelete disabled={empty} />
          </Button>
          <Button
            className={
              backButtonDisabled
                ? classes.buttonLargeDisabled
                : classes.buttonLarge
            }
            disabled={backButtonDisabled}
            onClick={this.previousListing}
          >
            <SwiperBack disabled={backButtonDisabled} />
          </Button>
          <Button
            className={
              empty ? classes.buttonLargeDisabled : classes.buttonLarge
            }
            onClick={this.handleFavoriteListing}
            disabled={empty}
          >
            <SwiperFavorites disabled={empty} />
            {this.state.showAnimation && (
              <CircularProgress size={78} className={classes.fabProgress} />
            )}
          </Button>
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: any) => ({
  token: state.auth.token,
});

const mapDispatchToProps = (dispatch: any) => ({
  dispatch,
  saveFavorite(listingId: number, token: string) {
    dispatch(saveFavorite(listingId, token));
  },
  blockListing(listingId: number, token: string) {
    dispatch(blockListing(listingId, token));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles)(Swiper));
